import { useState, useMemo } from 'react';
import { Loader2, MapPin } from 'lucide-react';
import type { ComputedSlot } from '@vagusx/clients';
import { groupSlotsByDate, extractTime } from '../utils/slot-grouping';
import { DateCarousel } from './DateCarousel';
import './slot-panel.css';

/** 4 columns × 2 rows = 8 slots visible by default per day */
const SLOTS_PER_ROW = 4;
const VISIBLE_ROWS = 2;
const INITIAL_VISIBLE = SLOTS_PER_ROW * VISIBLE_ROWS;
const MAX_DAYS = 3;

interface SlotResultsProps {
  slots: ComputedSlot[];
  isLoading: boolean;
  showBookButton: boolean;
  onBook: (slot: ComputedSlot) => void;
  computationMs?: number;
}

/**
 * Slot results — shows up to 3 days, each with 2 rows of slots
 * and an expandable "+N more" badge.
 *
 * Layout per day:
 *   Day Title
 *   [slot] [slot] [slot] [slot]   ← row 1
 *   [slot] [slot] [slot] [slot]   ← row 2
 *   [+5 more]                     ← expands to show rest
 */
export function SlotResults({
  slots,
  isLoading,
  showBookButton,
  onBook,
  computationMs,
}: SlotResultsProps): React.ReactElement {
  const groups = useMemo(() => groupSlotsByDate(slots), [slots]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  // The day the carousel jumped to. The sections render a MAX_DAYS window
  // starting there, so clicking a later date in the strip scrolls the list to it.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Resolve the selected date against the current groups (a prior search's date
  // may be gone) — fall back to the first day.
  const selectedIdx = selectedDate ? groups.findIndex((g) => g.date === selectedDate) : -1;
  const startIdx = selectedIdx >= 0 ? selectedIdx : 0;
  const effectiveSelectedDate = groups[startIdx]?.date ?? null;
  const visibleGroups = groups.slice(startIdx, startIdx + MAX_DAYS);

  function toggleExpand(date: string): void {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }

  // Hide location when all slots are at the same location
  const showLocation = useMemo(() => {
    if (slots.length <= 1) return false;
    const first = slots[0]?.locationName;
    return slots.some((s) => s.locationName !== first);
  }, [slots]);

  function handleBookSlot(slot: ComputedSlot): void {
    setSelectedSlotId(`${slot.startTime}-${slot.providerId}`);
    if (showBookButton) onBook(slot);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Searching for available slots...</span>
      </div>
    );
  }

  if (slots.length === 0) {
    return <div className="sp-empty">No availability for current filters.</div>;
  }

  return (
    <div className="sp-root">
      {/* Date carousel — clicking a day jumps the sections below to it. */}
      <DateCarousel
        groups={groups}
        selectedDate={effectiveSelectedDate}
        onSelectDate={setSelectedDate}
      />

      {/* Day sections */}
      {visibleGroups.map((group) => {
        const isExpanded = expandedDays.has(group.date);
        const visibleSlots = isExpanded ? group.slots : group.slots.slice(0, INITIAL_VISIBLE);
        const hiddenCount = group.slots.length - INITIAL_VISIBLE;

        return (
          <div key={group.date} className="sp-day-section">
            <div className="sp-day-title">{group.dayLabel}</div>

            <div className="sp-slot-grid">
              {visibleSlots.map((slot, idx) => (
                <button
                  key={`${slot.startTime}-${slot.providerId}-${idx}`}
                  type="button"
                  className={
                    selectedSlotId === `${slot.startTime}-${slot.providerId}`
                      ? 'sp-slot-card sp-slot-card-selected'
                      : 'sp-slot-card'
                  }
                  onClick={() => handleBookSlot(slot)}
                >
                  <div className="sp-slot-time">{extractTime(slot.displayTime)}</div>
                  <div className="sp-slot-provider">{slot.providerName}</div>
                  {showLocation && (
                    <div className="sp-slot-location">
                      <MapPin size={11} /> {slot.locationName}
                    </div>
                  )}
                </button>
              ))}
            </div>

            {hiddenCount > 0 && !isExpanded && (
              <button
                type="button"
                className="sp-more-count"
                onClick={() => toggleExpand(group.date)}
              >
                +{hiddenCount} more
              </button>
            )}

            {isExpanded && hiddenCount > 0 && (
              <button
                type="button"
                className="sp-more-count"
                onClick={() => toggleExpand(group.date)}
              >
                Show less
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
